import { Injectable, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import {
  NavigationDropdown,
  NavigationItem,
  NavigationLink,
  NavigationSubheading,
} from './navigation-item.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { PermisosService } from '../services/permisos.service';
import { MisPermisosResponse } from '../models/permiso.model';

/**
 * Fix 2026-09-29 — bug "perfil Empleado sin menu":
 *
 * El nav original chequeaba `cargo` contra un set fijo
 * (alumno/instructor/administrador/cajero/asesor) y cualquier user con otro
 * cargo (por ejemplo `empleado`) recibia solo el item "Inicio". Esta version
 * declara el menu COMPLETO como una lista tageada con `requiredPermiso`
 * (task #233 — permisos granulares) y filtra segun el pack del user actual:
 *
 *  - Superadmin → ve todo (no filtra).
 *  - Modo granular (`sin_permisos_asignados=false`) → filtra por
 *    `permisos_ids.includes(requiredPermiso)`. Un `empleado` con permisos
 *    5/7/9 (cajero-like) ve Tesoreria completa; sin permisos, no ve nada.
 *  - Modo legacy (`sin_permisos_asignados=true`) → fallback historico por
 *    cargo (`allowedCargos`). Preserva el comportamiento pre-#233 de los
 *    users que aun no tienen permisos granulares asignados.
 *
 * El menu de ALUMNO es especial: es plano y no chequea permisos (todo
 * alumno operativo tiene el mismo set).
 */
@Injectable({
  providedIn: 'root',
})
export class NavigationLoaderService {
  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);
  private readonly destroyRef = inject(DestroyRef);

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  constructor(
    private readonly layoutService: VexLayoutService,
    private readonly authService: AuthService,
    private readonly permisosService: PermisosService,
  ) {
    this.loadNavigation();

    // Recargar navegación cuando cambie el usuario (login/logout).
    // getMisPermisos() esta cacheado con shareReplay(1) y se invalida cuando
    // cambia el user, asi que un login nuevo dispara nueva request al backend.
    this.authService.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadNavigation());
  }

  loadNavigation(): void {
    const user = this.authService.currentUserValue;
    const cargo = (user?.cargo ?? '').toLowerCase();
    const isAlumno = cargo === 'alumno';

    // Alumno: menu plano, sin chequeo de permisos.
    if (isAlumno) {
      this._items.next(this.buildAlumnoMenu());
      return;
    }

    // Sin user (logout): menu vacio.
    if (!user) {
      this._items.next([]);
      return;
    }

    // Colaborador: construimos el menu maestro y filtramos por permisos.
    // El fetch de mis-permisos es async; mientras responde, mostramos SOLO
    // "Inicio" para no dejar la pantalla en blanco. En cuanto llega la
    // respuesta re-emitimos el menu final.
    this._items.next(this.buildColaboradorMenu({ soloInicio: true, cargo }));

    this.permisosService
      .getMisPermisos()
      .pipe(
        catchError((err) => {
          // Si el fetch de permisos falla (401, red caida) usamos el fallback
          // legacy por cargo, para no dejar al user sin operar.
          console.warn('[NavigationLoader] mis-permisos fallo, uso fallback legacy', err);
          return of<MisPermisosResponse>({
            doc: user.documento ?? '',
            is_superadmin: false,
            sin_permisos_asignados: true,
            permisos_ids: [],
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((permisos) => {
        this._items.next(
          this.buildColaboradorMenu({ soloInicio: false, cargo, permisos }),
        );
      });
  }

  // ── ALUMNO ────────────────────────────────────────────────────────────
  private buildAlumnoMenu(): NavigationItem[] {
    return [
      {
        type: 'link',
        label: 'Inicio',
        route: '/alumnos/home',
        icon: 'mat:home',
        routerLinkActiveOptions: { exact: true },
      },
      {
        type: 'link',
        label: 'Mi Horario',
        route: '/alumnos/mi-horario',
        icon: 'mat:schedule',
        routerLinkActiveOptions: { exact: true },
      },
      {
        type: 'link',
        label: 'Programación',
        route: '/alumnos/programacion',
        icon: 'mat:calendar_month',
        routerLinkActiveOptions: { exact: true },
      },
      {
        type: 'link',
        label: 'Mi Examen',
        route: '/alumnos/mi-examen',
        icon: 'mat:quiz',
        routerLinkActiveOptions: { exact: true },
      },
    ];
  }

  // ── COLABORADOR (admin / cajero / asesor / instructor / empleado / …) ─
  private buildColaboradorMenu(opts: {
    soloInicio: boolean;
    cargo: string;
    permisos?: MisPermisosResponse;
  }): NavigationItem[] {
    const { soloInicio, cargo, permisos } = opts;
    const master = this.buildMasterMenu();

    // Inicio siempre visible.
    const items: NavigationItem[] = [
      {
        type: 'link',
        label: 'Inicio',
        route: '/colaboradores/home',
        icon: 'mat:home',
        routerLinkActiveOptions: { exact: true },
      },
    ];

    if (soloInicio) {
      return items;
    }

    // Filtrar el resto del menu.
    const isSuperadmin = !!permisos?.is_superadmin;
    const legacy = !!permisos?.sin_permisos_asignados;
    const ids = new Set(permisos?.permisos_ids ?? []);

    for (const section of master) {
      const filtered = this.filterItem(section, {
        isSuperadmin,
        legacy,
        ids,
        cargo,
      });
      if (filtered) {
        items.push(filtered);
      }
    }

    return items;
  }

  /**
   * Filtra recursivamente un item segun el modo (granular/legacy/superadmin).
   * Devuelve null si el item queda oculto, o el item (posiblemente con
   * `children` filtrados) si queda visible. Un contenedor con 0 hijos
   * visibles se oculta tambien.
   */
  private filterItem(
    item: NavigationItem,
    ctx: {
      isSuperadmin: boolean;
      legacy: boolean;
      ids: Set<number>;
      cargo: string;
    },
  ): NavigationItem | null {
    // Contenedores: filtrar hijos, ocultar si quedan 0.
    if (item.type === 'subheading' || item.type === 'dropdown') {
      const children: Array<NavigationLink | NavigationDropdown> = [];
      for (const child of item.children) {
        const f = this.filterItem(child, ctx);
        if (f && (f.type === 'link' || f.type === 'dropdown')) {
          children.push(f);
        }
      }
      if (children.length === 0) return null;
      if (item.type === 'subheading') {
        return { ...item, children } as NavigationSubheading;
      }
      return { ...item, children } as NavigationDropdown;
    }

    // Link: chequeo puntual.
    return this.isVisible(item, ctx) ? item : null;
  }

  private isVisible(
    item: NavigationLink | NavigationDropdown,
    ctx: { isSuperadmin: boolean; legacy: boolean; ids: Set<number>; cargo: string },
  ): boolean {
    if (ctx.isSuperadmin) return true;

    if (ctx.legacy) {
      // Modo legacy: chequear allowedCargos.
      const allowed = item.allowedCargos ?? [];
      return allowed.includes('*') || allowed.includes(ctx.cargo);
    }

    // Modo granular: anyOfPermisos (si existe) tiene PRECEDENCIA sobre
    // requiredPermiso. Es el gate "cualquiera de estos" para secciones
    // donde el router usa un permiso catch-all pero el UI quiere ser mas
    // restrictivo (ver nota en navigation-item.interface.ts).
    if (item.anyOfPermisos && item.anyOfPermisos.length > 0) {
      return item.anyOfPermisos.some((p) => ctx.ids.has(p));
    }
    if (item.requiredPermiso == null) {
      // Item sin gate explicito → visible para cualquier user autenticado
      // que no sea legacy (cubre el "Inicio" y utilidades futuras).
      return true;
    }
    return ctx.ids.has(item.requiredPermiso);
  }

  // ── MENU MAESTRO (declarativo) ────────────────────────────────────────
  /**
   * Menu completo con cada item tageado con su `requiredPermiso` (para modo
   * granular) y `allowedCargos` (para modo legacy). El orden de las secciones
   * y la agrupacion son los mismos que la version pre-refactor, para que la
   * UX no cambie para admin/cajero/asesor/instructor.
   */
  private buildMasterMenu(): NavigationItem[] {
    return [
      // ── ACADÉMICO ────────────────────────────────────────────────────
      {
        type: 'subheading',
        label: 'Académico',
        children: [
          {
            type: 'link',
            label: 'Alumnos',
            route: '/admin/alumnos',
            icon: 'mat:person',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 10,
            allowedCargos: ['administrador', 'cajero', 'asesor', 'empleado'],
          },
          {
            type: 'link',
            label: 'Cursos',
            route: '/admin/cursos',
            icon: 'mat:school',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 1,
            allowedCargos: ['administrador', 'cajero', 'asesor', 'empleado'],
          },
          {
            type: 'link',
            label: 'Nuevo curso',
            route: '/admin/cursos/nuevo',
            icon: 'mat:add_circle',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 4,
            allowedCargos: ['administrador', 'cajero', 'asesor', 'empleado'],
          },
        ],
      },

      // ── CLASES DE TEORÍA ─────────────────────────────────────────────
      // Fix 2026-10-01: anyOfPermisos en vez de requiredPermiso: 1. El
      // permiso #1 es catch-all operativo (cursos tambien lo usa), asi que
      // un cajero-cursos con solo #1 veia esta seccion. Ahora exige al menos
      // uno de: #37 (sanciones/instructor), #38 (crear bloques), #39 (bulk
      // bloques). Un empleado-cursos sin esos no ve nada aqui.
      {
        type: 'subheading',
        label: 'Clases de Teoría',
        children: [
          {
            type: 'link',
            label: 'Mi clase ahora',
            route: '/colaboradores/mi-clase-ahora',
            icon: 'mat:play_circle',
            routerLinkActiveOptions: { exact: true },
            anyOfPermisos: [37],
            allowedCargos: ['administrador', 'instructor'],
          },
          {
            type: 'link',
            label: 'Programación Bloques',
            route: '/colaboradores/programacion',
            icon: 'mat:menu_book',
            routerLinkActiveOptions: { exact: true },
            anyOfPermisos: [38, 39],
            allowedCargos: ['administrador', 'instructor'],
          },
          {
            type: 'link',
            label: 'Consulta de Clases',
            route: '/colaboradores/consulta-clases',
            icon: 'mat:search',
            routerLinkActiveOptions: { exact: true },
            anyOfPermisos: [37, 38],
            allowedCargos: ['administrador', 'instructor'],
          },
          {
            type: 'dropdown',
            label: 'Examen CALE',
            icon: 'mat:quiz',
            allowedCargos: ['administrador'],
            children: [
              {
                type: 'link',
                label: 'Configuración',
                route: '/admin/examenes',
                icon: 'mat:tune',
                routerLinkActiveOptions: { exact: true },
                requiredPermiso: 24,
                allowedCargos: ['administrador'],
              },
              {
                type: 'link',
                label: 'Banco de preguntas',
                route: '/admin/examenes/preguntas',
                icon: 'mat:list_alt',
                routerLinkActiveOptions: { exact: true },
                requiredPermiso: 24,
                allowedCargos: ['administrador'],
              },
              {
                type: 'link',
                label: 'Reportes',
                route: '/admin/examenes/reportes',
                icon: 'mat:analytics',
                routerLinkActiveOptions: { exact: true },
                requiredPermiso: 32,
                allowedCargos: ['administrador'],
              },
            ],
          },
        ],
      },

      // ── CLASES PRÁCTICAS ─────────────────────────────────────────────
      // Fix 2026-10-01: mismo criterio que Teoria — anyOfPermisos para no
      // exponer la seccion al cajero-cursos que solo tiene #1.
      {
        type: 'subheading',
        label: 'Clases Prácticas',
        children: [
          {
            type: 'link',
            label: 'Programación Clases',
            route: '/admin/practicas/agendar',
            icon: 'mat:calendar_month',
            routerLinkActiveOptions: { exact: true },
            anyOfPermisos: [38],
            allowedCargos: ['administrador'],
          },
          {
            type: 'link',
            label: 'Consultar Clases',
            route: '/admin/practicas',
            icon: 'mat:search',
            routerLinkActiveOptions: { exact: true },
            anyOfPermisos: [37, 38],
            allowedCargos: ['administrador'],
          },
          {
            type: 'link',
            label: 'Gestionar Clases',
            route: '/colaboradores/mis-practicas',
            icon: 'mat:sports_motorsports',
            routerLinkActiveOptions: { exact: true },
            anyOfPermisos: [37, 38],
            allowedCargos: ['administrador'],
          },
          {
            type: 'link',
            label: 'Mis Clases Prácticas',
            route: '/colaboradores/mis-practicas',
            icon: 'mat:directions_car',
            routerLinkActiveOptions: { exact: true },
            anyOfPermisos: [37],
            allowedCargos: ['instructor'],
          },
        ],
      },

      // ── TESORERÍA ────────────────────────────────────────────────────
      {
        type: 'subheading',
        label: 'Tesorería',
        children: [
          {
            type: 'link',
            label: 'Reportes',
            route: '/admin/reportes',
            icon: 'mat:assessment',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 32,
            allowedCargos: ['administrador', 'cajero', 'empleado'],
          },
          {
            type: 'link',
            label: 'Cartera',
            route: '/admin/cartera',
            icon: 'mat:account_balance_wallet',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 28,
            allowedCargos: ['administrador', 'cajero', 'empleado'],
          },
          {
            // Reporte Cartera 50/50 (F4 2026-10-05): charts + export +
            // envio recordatorios. Backend exige require_permiso(43).
            type: 'link',
            label: 'Reporte 50/50',
            route: '/admin/reportes/cartera-estado-pago',
            icon: 'mat:receipt_long',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 43,
            allowedCargos: ['administrador'],
          },
          {
            type: 'link',
            label: 'Cajas y Bancos',
            route: '/admin/tesoreria/cajas-bancos',
            icon: 'mat:account_balance',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 5,
            allowedCargos: ['administrador', 'cajero', 'empleado'],
          },
          {
            type: 'link',
            label: 'Historial de Traslados',
            route: '/admin/tesoreria/cajas-bancos/traslados',
            icon: 'mat:swap_horiz',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 5,
            allowedCargos: ['administrador', 'cajero', 'empleado'],
          },
          {
            type: 'link',
            label: 'Recibos de Caja',
            route: '/admin/tesoreria/recibos-caja',
            icon: 'mat:receipt_long',
            routerLinkActiveOptions: { exact: true },
            // Fix 2026-10-15 (Cristina/Graciela): antes era requiredPermiso: 7
            // (ANULAR), lo que ocultaba el link a cualquier cajero que solo
            // tuviera #6 (CREAR). anyOfPermisos abre a quien pueda crear o
            // anular — el backend ya hace su propio gate por operacion.
            anyOfPermisos: [6, 7],
            allowedCargos: ['administrador', 'cajero', 'empleado'],
          },
          {
            type: 'link',
            label: 'Recibos de Egresos',
            route: '/admin/tesoreria/recibos-egresos',
            icon: 'mat:money_off',
            routerLinkActiveOptions: { exact: true },
            // Gemelo del fix de Recibos de Caja: antes requiredPermiso: 9
            // (ANULAR). anyOfPermisos abre a crear (#8) o anular (#9).
            anyOfPermisos: [8, 9],
            allowedCargos: ['administrador', 'cajero', 'empleado'],
          },
          {
            type: 'link',
            label: 'Turnos',
            route: '/admin/tesoreria/turnos',
            icon: 'mat:point_of_sale',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 5,
            allowedCargos: ['administrador', 'cajero', 'empleado'],
          },
        ],
      },

      // ── SOPORTE ──────────────────────────────────────────────────────
      // Link top-level entre Tesoreria y Administracion (fix 2026-10-03).
      // Visible a cualquier colaborador no-alumno sin gate de permiso: el
      // backend filtra con require_colaborador que rechaza alumnos. En modo
      // legacy, '*' significa cualquier cargo.
      {
        type: 'link',
        label: 'Soporte',
        route: '/colaboradores/mi-soporte',
        icon: 'mat:support_agent',
        routerLinkActiveOptions: { exact: true },
        allowedCargos: ['*'],
      },

      // ── ADMINISTRACIÓN ───────────────────────────────────────────────
      {
        type: 'subheading',
        label: 'Administración',
        children: [
          {
            // Sweep 2026-10-22 (Task 10): backed por dashboard.py
            // informes-pyg* (3 endpoints), que ahora exige #46 (INFORMES),
            // no #32 — ese era el permiso del reporte legacy de Tesoreria
            // (reportes_admin.py). Un colaborador con solo #46 no veia el
            // link antes de este fix.
            type: 'link',
            label: 'Informes',
            route: '/admin/informes',
            icon: 'mat:assessment',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 46,
            allowedCargos: ['administrador'],
          },
          {
            type: 'link',
            label: 'Usuarios',
            route: '/admin/usuarios',
            icon: 'mat:manage_accounts',
            routerLinkActiveOptions: { exact: true },
            // Usuarios queda gated a superadmin en el backend; en modo
            // granular ningun permiso_id lo abre — solo el flag is_superadmin
            // (chequeado antes en isVisible). En modo legacy, admin lo ve.
            // Revisado en el sweep 2026-10-22 (Task 10): sigue sin permiso
            // numerico a proposito — require_superadmin, no require_permiso.
            requiredPermiso: undefined,
            allowedCargos: ['administrador'],
          },
          {
            // IP whitelist super_admin (feature 2026-10-01). Mismo criterio
            // que Usuarios: backend exige super_admin, en front lo gateamos
            // por 'administrador' y el 403 corta al no-superadmin. Revisado
            // en el sweep 2026-10-22 (Task 10): ip_whitelist.py sigue 100%
            // require_superadmin, sin permiso numerico que mapear.
            type: 'link',
            label: 'Seguridad / IPs autorizadas',
            route: '/admin/ip-whitelist',
            icon: 'mat:shield',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: undefined,
            allowedCargos: ['administrador'],
          },
          {
            // Panel soporte MCD (F1 2026-10-02). Lista tickets de todo el tenant,
            // cambia estado, ve capturas. Backend exige require_admin +
            // require_permiso(44) desde la migracion de catalogo granular
            // (Task 1, commit 4569c04) — antes quedaba sin gate numerico y
            // en modo granular ningun permiso lo abria (solo superadmin).
            // Sweep 2026-10-22 (Task 10): ahora un admin con #44 lo ve.
            type: 'link',
            label: 'Tickets de soporte',
            route: '/admin/soporte',
            icon: 'mat:support_agent',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 44,
            allowedCargos: ['administrador'],
          },
          {
            // Membresia soporte (F2a 2026-10-03). Pagos mensuales + cortesia.
            // Backend exige require_admin + require_permiso(45) desde la
            // migracion de catalogo granular (Task 1). Mismo fix que
            // "Tickets de soporte" arriba — sweep 2026-10-22 (Task 10).
            // El flujo de VALIDAR el pago sigue siendo require_superadmin
            // (admin_membresia.py), pero el link en si solo necesita #45
            // para ver/gestionar la membresia del propio tenant.
            type: 'link',
            label: 'Membresía soporte',
            route: '/admin/soporte/membresia',
            icon: 'mat:workspace_premium',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 45,
            allowedCargos: ['administrador'],
          },
          {
            // Revisado en el sweep 2026-10-22 (Task 10): backed por
            // manuales_admin.py (CRUD), que sigue en #24 (CONFIGURACION) —
            // la migracion de catalogo granular no lo tocó. El #43
            // CATALOGOS nuevo es para el GET publico de lectura
            // (manuales.py, consumido por ManualBtnComponent), no para este link.
            type: 'link',
            label: 'Manuales',
            route: '/admin/manuales',
            icon: 'mat:import_contacts',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 24,
            allowedCargos: ['administrador'],
          },
          {
            type: 'link',
            label: 'Notificaciones',
            route: '/admin/notificaciones',
            icon: 'mat:mark_email_unread',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 32,
            allowedCargos: ['administrador'],
          },
          {
            type: 'link',
            label: 'Configuración de Cursos',
            route: '/admin/configuracion-cursos',
            icon: 'mat:price_change',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 24,
            allowedCargos: ['administrador'],
          },
          {
            type: 'link',
            label: 'Configuración de Contratos',
            route: '/admin/contratos/config',
            icon: 'mat:gavel',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 24,
            allowedCargos: ['administrador'],
          },
          {
            type: 'link',
            label: 'Configuración de Bloques',
            route: '/admin/configuracion-bloques',
            icon: 'mat:settings',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 24,
            allowedCargos: ['administrador'],
          },
          {
            type: 'link',
            label: 'Bloques de Teoría',
            route: '/admin/bloques-teoria',
            icon: 'mat:menu_book',
            routerLinkActiveOptions: { exact: true },
            // Fix 2026-10-01: era #1 (catch-all), lo elevamos a #38/39
            // para que un cajero-cursos no lo vea.
            anyOfPermisos: [38, 39],
            allowedCargos: ['administrador'],
          },
          {
            type: 'link',
            label: 'Configuración de Prácticas',
            route: '/admin/configuracion-practicas',
            icon: 'mat:tune',
            routerLinkActiveOptions: { exact: true },
            requiredPermiso: 24,
            allowedCargos: ['administrador'],
          },
          {
            type: 'dropdown',
            label: 'Catálogos',
            icon: 'mat:category',
            allowedCargos: ['administrador'],
            children: [
              {
                type: 'link',
                label: 'Servicios',
                route: '/admin/servicios',
                icon: 'mat:receipt_long',
                routerLinkActiveOptions: { exact: true },
                requiredPermiso: 24,
                allowedCargos: ['administrador'],
              },
              {
                type: 'link',
                label: 'Paquetes de Clases',
                route: '/admin/paquetes',
                icon: 'mat:inventory_2',
                routerLinkActiveOptions: { exact: true },
                requiredPermiso: 24,
                allowedCargos: ['administrador'],
              },
              {
                type: 'link',
                label: 'Convenios Médicos',
                route: '/admin/convenios-medicos',
                icon: 'mat:medical_services',
                routerLinkActiveOptions: { exact: true },
                requiredPermiso: 24,
                allowedCargos: ['administrador'],
              },
              {
                type: 'link',
                label: 'Vehículos',
                route: '/admin/vehiculos',
                icon: 'mat:directions_car',
                routerLinkActiveOptions: { exact: true },
                requiredPermiso: 23,
                allowedCargos: ['administrador'],
              },
              {
                type: 'link',
                label: 'Instructores',
                route: '/admin/instructores',
                icon: 'mat:badge',
                routerLinkActiveOptions: { exact: true },
                requiredPermiso: 21,
                allowedCargos: ['administrador'],
              },
              {
                type: 'link',
                label: 'Terceros',
                route: '/admin/terceros',
                icon: 'mat:store',
                routerLinkActiveOptions: { exact: true },
                requiredPermiso: 14,
                allowedCargos: ['administrador'],
              },
              {
                type: 'link',
                label: 'Asesores',
                route: '/admin/asesores',
                icon: 'mat:support_agent',
                routerLinkActiveOptions: { exact: true },
                requiredPermiso: 22,
                allowedCargos: ['administrador'],
              },
            ],
          },
        ],
      },
    ];
  }
}
